<!-- NavPanelHeader.vue — the kicker + title strip shown identically atop
     NavRail's hover-preview flyout and the persisted tree panel, so nothing
     visually changes (no "jump") once a hovered section becomes the
     committed, active one. An optional slot on the right holds an action
     (only the persisted panel uses it, for the sidebar-hide toggle). The
     section's ALPHA/BETA tag (if any) now lives here too, next to the
     title, rather than on the rail row it was hovered from. The action
     column's width is reserved unconditionally — even in the flyout, which
     never fills it — so the title/tag never get more or less room to wrap
     into depending on whether an action happens to be slotted in. -->
<template>
    <div class="nav-panel-header">
        <div class="nav-panel-header__text">
            <div class="nav-panel-header__kicker">{{ kicker }}</div>
            <div class="nav-panel-header__title-row">
                <div class="nav-panel-header__title">{{ title }}</div>
                <span v-if="tag" class="nav-panel-header__tag">{{ tag }}</span>
            </div>
        </div>
        <div class="nav-panel-header__action">
            <slot />
        </div>
    </div>
</template>

<script setup lang="ts">
defineProps<{ kicker: string; title: string; tag?: string }>();
</script>

<style scoped lang="scss">
.nav-panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    padding: 11px 14px 9px;
    border-bottom: 1px solid var(--surface-border, #dee2e6);
    flex: 0 0 auto;
    text-align: left;
}
.nav-panel-header__kicker {
    font-size: 9px;
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: var(--text-color-secondary);
}
.nav-panel-header__title-row {
    display: flex;
    align-items: center;
    // At the panel's default/narrower widths, "<title> <TAG>" together can
    // outgrow the row — wrap the tag onto its own line below rather than
    // letting the title's own text break mid-word (see .nav-panel-header__title).
    flex-wrap: wrap;
    gap: 6px;
}
.nav-panel-header__title {
    font-size: 15px;
    font-weight: 800;
    margin-top: 1px;
    white-space: nowrap;
}
// Matches ApplicationLayout.vue's .nav-panel-toggle exactly — reserved
// whether or not the slot actually has that button in it (see template note).
.nav-panel-header__action {
    width: 26px;
    height: 26px;
    flex: 0 0 auto;
}
// The same outline-pill convention the top bar's own LIVE/release/draft
// badges use (ApplicationLayout.vue's .live-badge/.release-badge/.thread-badge)
// — reused here rather than inventing a new one. Neutral (the kicker's own
// secondary color) since ALPHA/BETA is an early-access flag, not tied to any
// one mode's accent color.
.nav-panel-header__tag {
    flex: 0 0 auto;
    font-size: 0.6em;
    font-weight: 700;
    padding: 0.15em 0.45em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-color-secondary);
    border: 1px solid var(--text-color-secondary);
    background: transparent;
}
</style>
